import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

// material
import { styled } from '@mui/material/styles';
import { Box, Link, Button, Drawer, Typography, Avatar, Stack, useTheme } from '@mui/material';
// components
import { useSelector } from 'react-redux';
import Scrollbar from '../../components/Scrollbar';
import NavSection from '../../components/NavSection';
import { MHidden } from '../../components/@material-extend';
//
import sidebarConfig from './SidebarConfig';
import DashboardClubChoice from './DashboardClubChoice';
import { useTranslation } from '../../common/LocalizationProvider';
import {
  selectAbonnementData,
  selectGroupSelectedId,
  selectSession
} from '../../createSelectorUtils';

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 250;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH
  }
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.shape.borderRadiusSm,
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]
}));

const LogoStyle = styled(Box)(({ theme }) => ({
  minWidth: 240,
  [theme.breakpoints.up('sm')]: {
    minWidth: 240
  }
}));
const StackIll = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200]
}));
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const t = useTranslation();
  const navigate = useNavigate();

  const session = useSelector(selectSession);
  const abonnement = useSelector(selectAbonnementData);
  const groupId = useSelector(selectGroupSelectedId);
  const permission = session.permission
    ? session.permission.find((item) => item.groupid === groupId) || {}
    : {};
  const { pathname } = useLocation();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const correspondLevel = (level) => {
    switch (level) {
      case 'student':
        return t('fields_Student');
      case 'studentSolo':
        return `${t('fields_Student_Solo')} (+500 points)`;
      case 'licensedPilot':
        return `${t('fields_Licensed_Pilot')} (+1500 points)`;
      case 'instructor':
        return `${t('fields_Instructor')} (+5000 points)`;
      default:
        return '';
    }
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: '100%',
        '& .simplebar-content': { height: '100%', display: 'flex', flexDirection: 'column' }
      }}
    >
      <Box sx={{ px: 2.5, py: 3 }}>
        <Stack alignItems="center">
          <LogoStyle>
            {theme.palette.mode === 'dark' ? (
              <img src="/static/cto-image/icon/logoSimpleDarkMode.svg" alt="login" />
            ) : (
              <img src="/static/cto-image/icon/logoSimple.svg" alt="login" />
            )}
          </LogoStyle>
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, py: 1 }}>
        <Link underline="none" component={RouterLink} to="/profile">
          <AccountStyle>
            <Avatar src={session.userImage} alt="photoURL" />
            <Box sx={{ ml: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                {`${session.prenom} ${session.name}`}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {correspondLevel(session.niveau)}
              </Typography>
            </Box>
          </AccountStyle>
        </Link>
      </Box>

      {!session.administrator && <DashboardClubChoice />}

      <NavSection navConfig={sidebarConfig} />

      <Box sx={{ flexGrow: 1 }} />

      {(permission.status === 3 || permission.status === 4 || groupId === 0) && !abonnement && (
        <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
          <StackIll
            alignItems="center"
            spacing={3}
            sx={{
              p: 2.5,
              pt: 5,
              borderRadius: 2,
              position: 'relative'
            }}
          >
            <Box
              component="img"
              src="/static/illustrations/illustration_avatar.png"
              sx={{ width: 200, position: 'absolute', top: -80 }}
            />

            <Box sx={{ textAlign: 'center' }}>
              <Typography gutterBottom variant="h6">
                {t('DB_SideBar_GetMore')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {t('DB_SideBar_OneWeekFreeTrial')}
              </Typography>
            </Box>

            <Button
              fullWidth
              onClick={() => navigate('/subscription/plans', { replace: true })}
              variant="contained"
            >
              {t('DB_SideBar_HaveATry')}
            </Button>
          </StackIll>
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: 'background.default'
            }
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
